// src/firebaseUtils.js - COMPLETE DELETION SAFE VERSION

import { db, auth } from "./firebase-config";
import {
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  deleteUser,
} from "firebase/auth";

export const registerNewStudent = async (email, password) => {
  if (password.length < 6) {
    return { success: false, message: "Password must be at least 6 characters." };
  }

  let authUser = null;

  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    authUser = userCredential.user;
    const userId = authUser.uid;

    await setDoc(doc(db, "users", userId), {
      id: userId,
      email,
      role: "student",
      enabled: true,
      hasAiAccess: false,
      authorizedCourses: [],
      progress: {},
    });

    return {
      success: true,
      message: `Student ${email} registered successfully.`,
    };
  } catch (error) {
    console.error("Registration failed:", error);

    if (authUser) {
      await deleteUser(authUser);
    }

    return { success: false, message: error.message };
  }
};

export const toggleUserStatus = async (userId, currentStatus) => {
  try {
    await updateDoc(doc(db, "users", userId), {
      enabled: !currentStatus,
    });

    return {
      success: true,
      message: `User ${!currentStatus ? "enabled" : "disabled"}.`,
    };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const toggleCourseAccess = async (userId, courseId, hasAccess) => {
  try {
    await updateDoc(doc(db, "users", userId), {
      authorizedCourses: hasAccess
        ? arrayRemove(courseId)
        : arrayUnion(courseId),
    });

    return {
      success: true,
      message: `Course access ${hasAccess ? "revoked" : "granted"}.`,
    };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const toggleAiAccess = async (userId, currentAccess) => {
  try {
    await updateDoc(doc(db, "users", userId), {
      hasAiAccess: !currentAccess,
    });

    return {
      success: true,
      message: `AI Interview access ${!currentAccess ? "granted" : "revoked"}.`,
    };
  } catch (error) {
    console.error("Error toggling AI access:", error);
    return { success: false, message: error.message };
  }
};

export const updateCourseInFirestore = async (courseData) => {
  try {
    const { id, name, description, folders = [] } = courseData;

    await setDoc(
      doc(db, "courses", id),
      {
        id,
        name,
        description,
        folders,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await setDoc(
      doc(db, "courseContent", id),
      {
        courseId: id,
        folders,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return {
      success: true,
      message: `Course "${name}" saved successfully.`,
    };
  } catch (error) {
    console.error("Course update failed:", error);
    return { success: false, message: error.message };
  }
};

export const completeLesson = async (
  currentUserId,
  courseName,
  folderIndex,
  lessonIndex,
  userProgress
) => {
  try {
    const progressKey = `${courseName}_${folderIndex}`;
    const completed = userProgress?.[progressKey] || [];

    if (completed.includes(lessonIndex)) {
      return { success: true };
    }

    await updateDoc(doc(db, "users", currentUserId), {
      progress: {
        ...userProgress,
        [progressKey]: [...completed, lessonIndex],
      },
    });

    return { success: true };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const isLessonCompleted = (
  userProgress,
  courseName,
  folderIndex,
  lessonIndex
) => {
  const key = `${courseName}_${folderIndex}`;
  return userProgress?.[key]?.includes(lessonIndex);
};
